import {usePage} from '@inertiajs/vue3';

// Global state - calculated once and shared across all components
let CLOCK_OFFSET = 0;
let CLOCK_OFFSET_INITIALIZED = false;

/**
 * Composable that provides server-synchronized time.
 *
 * Calculates the clock offset between client and server once on first use,
 * then caches it for subsequent calls. This ensures accurate time calculations
 * even when the client's system clock is incorrect.
 *
 * The offset is calculated as: clientTime - serverTime
 * - Positive offset: client is ahead of server
 * - Negative offset: client is behind server
 *
 * @param serverTime - Optional server time override for testing
 * @returns Object with clockOffset
 */
export function getClockOffset(serverTime?: string) {
  // Only initialize once, unless serverTime is explicitly provided (for testing)
  if (!CLOCK_OFFSET_INITIALIZED || serverTime) {
    const page = usePage();
    const effectiveServerTime = serverTime ?? page?.props?.serverTime;

    if (effectiveServerTime) {
      const serverTimeMs = new Date(effectiveServerTime).getTime();
      const clientTimeMs = Date.now();
      const calculatedOffset = clientTimeMs - serverTimeMs;

      // Only apply offset if it exceeds tolerance threshold
      // Small differences are likely due to network latency or page load time.
      const clockSkewToleranceInMs = 10000; // 10s

      if (Math.abs(calculatedOffset) > clockSkewToleranceInMs) {
        CLOCK_OFFSET = calculatedOffset;
      } else {
        CLOCK_OFFSET = 0;
      }
    } else {
      // No server time available, assume clocks are in sync
      CLOCK_OFFSET = 0;
    }

    // Mark as initialized (even in testing mode when serverTime is provided)
    // This ensures the offset is calculated and cached
    CLOCK_OFFSET_INITIALIZED = true;
  }

  return {
    /**
     * The calculated clock offset in milliseconds
     */
    clockOffset: CLOCK_OFFSET,
  };
}

/**
 * Reset the server time initialization state.
 * Primarily used for testing to allow recalculation of clock offset.
 */
export function resetClockOffset() {
  CLOCK_OFFSET_INITIALIZED = false;
  CLOCK_OFFSET = 0;
}
